/**
 * TEMP-H-017: Theme Persistence Helper
 * 
 * Backend persistence for user theme preferences with graceful fallbacks.
 */

import type { ThemeId } from '@/theme/types';
import { getApiHeadersGet, getApiHeadersJson } from './api-base';
import { joinApiPath } from './api-config';
import { whenAuthReady, isAuthReady } from './auth-readiness';

/**
 * Backend theme persistence configuration
 */
const THEME_PERSISTENCE_CONFIG = {
  endpoints: {
    setTheme: 'me/preferences/theme',
    getPreferences: 'me/preferences',
    getAvailableThemes: 'ui/themes'
  },
  timeout: 5000, // 5 second timeout for non-blocking calls
} as const;

/**
 * User theme preferences from backend
 */
export interface UserThemePreferences {
  activeThemeId: ThemeId;
  updatedAt?: string;
}

/**
 * Available theme from backend API
 */
export interface BackendTheme {
  id: string;
  name: string;
  description: string;
  version?: string;
  author?: string;
}

/**
 * Persist theme selection to backend (best-effort, non-blocking)
 * DV-AUTH-004: Defers backend writes until auth check is complete
 */
export async function persistTheme(themeId: ThemeId): Promise<void> {
  const actualPersist = async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), THEME_PERSISTENCE_CONFIG.timeout);

  try {
    const response = await fetch(joinApiPath(THEME_PERSISTENCE_CONFIG.endpoints.setTheme), {
      method: 'PATCH',
      credentials: 'include', // Include session cookies
      headers: getApiHeadersJson(), // DV-AUTH-004: JSON headers for PATCH requests
      body: JSON.stringify({ themeId }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      // Silently handle 404s (backend endpoint not implemented yet)
      if (response.status === 404) {
        if (import.meta.env.DEV) {
          console.log('💡 Theme persistence endpoint not available (this is normal)');
        }
        return; // Graceful fallback to localStorage only
      }

      // Silently handle 400s (bad request - likely malformed data or invalid theme)
      if (response.status === 400) {
        if (import.meta.env.DEV) {
          console.debug('💡 Theme persistence failed - bad request (falling back to localStorage)');
        }
        return; // Graceful fallback to localStorage only
      }

      // Log other errors in development
      if (import.meta.env.DEV) {
        const errorText = await response.text().catch(() => 'Unknown error');
        console.warn(`Theme persistence failed (${response.status}):`, errorText);
      }

      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    // Successful persistence
    if (import.meta.env.DEV) {
      console.log(`✅ Theme "${themeId}" persisted to backend`);
    }

  } catch (error) {
    clearTimeout(timeoutId);

    // Handle different error types gracefully
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new Error('Theme persistence timed out');
      } else if (error.message.includes('NetworkError') || error.message.includes('fetch')) {
        throw new Error('Network error during theme persistence');
      }
    }

    // Re-throw for caller to handle
    throw error;
  }
  };

  // DV-AUTH-004: Gate backend theme persistence behind auth readiness
  if (isAuthReady()) {
    // Auth check complete, persist immediately
    return actualPersist();
  } else {
    // Defer until auth check is complete
    if (import.meta.env.DEV) {
      console.log(`🎨 Deferring backend theme "${themeId}" persistence until auth ready`);
    }
    return new Promise<void>((resolve, reject) => {
      whenAuthReady(async () => {
        try {
          await actualPersist();
          resolve();
        } catch (error) {
          reject(error);
        }
      }, `backend-theme-persist-${themeId}`);
    });
  }
}

/**
 * Fetch user theme preferences from backend (optional enhancement)
 */
export async function fetchUserThemePreferences(): Promise<UserThemePreferences | null> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), THEME_PERSISTENCE_CONFIG.timeout);

  try {
    const response = await fetch(joinApiPath(THEME_PERSISTENCE_CONFIG.endpoints.getPreferences), {
      method: 'GET',
      credentials: 'include',
      headers: getApiHeadersGet(), // DV-AUTH-004: Preflight-safe headers for GET requests
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (response.status === 404) {
      // User has no preferences yet - this is normal
      return null;
    }

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const preferences = await response.json();
    
    // Validate response structure
    if (preferences && typeof preferences.activeThemeId === 'string') {
      return preferences;
    }

    if (import.meta.env.DEV) {
      console.warn('Invalid theme preferences response:', preferences);
    }
    
    return null;

  } catch (error) {
    clearTimeout(timeoutId);

    if (import.meta.env.DEV) {
      console.warn('Failed to fetch user theme preferences:', error);
    }

    // Return null for graceful fallback to localStorage
    return null;
  }
}

/**
 * Fetch available themes from backend (future enhancement)
 */
export async function fetchAvailableThemes(): Promise<BackendTheme[] | null> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), THEME_PERSISTENCE_CONFIG.timeout);

  try {
    const response = await fetch(joinApiPath(THEME_PERSISTENCE_CONFIG.endpoints.getAvailableThemes), {
      method: 'GET',
      credentials: 'include',
      headers: getApiHeadersGet(), // DV-AUTH-004: Preflight-safe headers for GET requests
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (response.status === 404) {
      // Endpoint not implemented yet
      return null;
    }

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const themes = await response.json();
    
    // Validate response structure
    if (Array.isArray(themes)) {
      return themes.filter(theme => 
        theme && 
        typeof theme.id === 'string' && 
        typeof theme.name === 'string'
      );
    }

    return null;

  } catch (error) {
    clearTimeout(timeoutId);

    if (import.meta.env.DEV) {
      console.warn('Failed to fetch available themes from backend:', error);
    }

    // Return null for graceful fallback to local themes
    return null;
  }
}

/**
 * Check if theme persistence is available
 */
export async function isThemePersistenceAvailable(): Promise<boolean> {
  try {
    const response = await fetch(joinApiPath(THEME_PERSISTENCE_CONFIG.endpoints.setTheme), {
      method: 'OPTIONS',
      credentials: 'include',
      headers: getApiHeadersGet(), // DV-AUTH-004: Preflight-safe headers for OPTIONS requests
      signal: AbortSignal.timeout(2000), // Quick check
    });

    return response.ok || response.status === 405; // OK or Method Not Allowed (endpoint exists)
  } catch {
    return false;
  }
}

/**
 * Theme persistence error types
 */
export class ThemePersistenceError extends Error {
  constructor(
    message: string,
    public readonly type: 'network' | 'timeout' | 'server' | 'validation' = 'server',
    public readonly statusCode?: number
  ) {
    super(message);
    this.name = 'ThemePersistenceError';
  }
}

/**
 * Retry theme persistence with exponential backoff (for critical flows)
 */
export async function persistThemeWithRetry(
  themeId: ThemeId, 
  maxRetries = 3,
  baseDelay = 1000
): Promise<void> {
  let lastError: Error = new Error('Failed to persist theme after retries');

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      await persistTheme(themeId);
      return; // Success
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown error');

      // Don't retry on certain errors
      if (lastError.message.includes('400') || lastError.message.includes('401') || lastError.message.includes('403')) {
        throw lastError;
      }

      // Don't retry on last attempt
      if (attempt === maxRetries) {
        break;
      }

      // Wait with exponential backoff
      const delay = baseDelay * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, delay));

      if (import.meta.env.DEV) {
        console.log(`Retrying theme persistence (attempt ${attempt + 2}/${maxRetries + 1}) in ${delay}ms`);
      }
    }
  }

  // All retries failed
  throw lastError;
}